// medplum-bounded-search.js — honest _offset pagination for high-volume worklists.
// Caps at PAGE_SIZE × MAX_PAGES (default 1000 primary rows) and surfaces
// `truncated: true` when the budget is exhausted — never silently omit rows.

export const SEARCH_PAGE_SIZE = 200;
export const SEARCH_MAX_PAGES = 5;
export const SEARCH_MAX_ROWS = SEARCH_PAGE_SIZE * SEARCH_MAX_PAGES;

function countPrimary(entries, resourceType) {
  return entries.filter((e) => e.resource && e.resource.resourceType === resourceType).length;
}

/** Paged Encounter search preserving _include/_revinclude on every page. */
export async function searchEncounterBundleBounded(medplum, baseQuery, { primaryType = 'Encounter', maxPages = SEARCH_MAX_PAGES } = {}) {
  const allEntries = [];
  let offset = 0;
  let truncated = false;
  let primaryCount = 0;
  const pageBudget = Math.max(1, maxPages);

  for (let page = 0; page < pageBudget; page++) {
    const query = `${baseQuery}&_count=${SEARCH_PAGE_SIZE}&_offset=${offset}`;
    const bundle = await medplum.search(primaryType, query);
    const entries = (bundle && bundle.entry) || [];
    allEntries.push(...entries);

    const pagePrimary = countPrimary(entries, primaryType);
    primaryCount += pagePrimary;

    if (pagePrimary < SEARCH_PAGE_SIZE) break;
    if (page >= pageBudget - 1) {
      truncated = true;
      break;
    }
    offset += SEARCH_PAGE_SIZE;
  }

  return {
    bundle: { resourceType: 'Bundle', type: 'searchset', entry: allEntries },
    truncated,
    fetchedCount: primaryCount,
    maxRows: SEARCH_MAX_ROWS,
  };
}

/** Paged searchResources (Task, Practitioner, …) with the same budget + flag. */
export async function searchResourcesBounded(medplum, resourceType, baseQuery, { maxPages = SEARCH_MAX_PAGES } = {}) {
  const all = [];
  let offset = 0;
  let truncated = false;
  const pageBudget = Math.max(1, maxPages);

  for (let page = 0; page < pageBudget; page++) {
    const sep = baseQuery ? '&' : '';
    const query = `${baseQuery}${sep}_count=${SEARCH_PAGE_SIZE}&_offset=${offset}`;
    const raw = await medplum.searchResources(resourceType, query);
    const pageRows = Array.isArray(raw) ? Array.from(raw) : [];
    all.push(...pageRows);

    if (pageRows.length < SEARCH_PAGE_SIZE) break;
    if (page >= pageBudget - 1) {
      truncated = true;
      break;
    }
    offset += SEARCH_PAGE_SIZE;
  }

  return { resources: all, truncated, fetchedCount: all.length, maxRows: SEARCH_MAX_ROWS };
}

export function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ── GraphQL worklist (two bounded round-trips per page) ─────────────────────
// Replaces REST Encounter + _include(Patient) + _revinclude(Composition) with
// $graphql: per pagination page, one EncounterList call (Patients fold in via
// the `subject.resource` field) then one CompositionList call bounded to that
// page's encounter ids (was an unfiltered CompositionList(_count:1000) over the
// whole project). This is the ONLY worklist read path (no REST fallback — see
// loadEncounterWorklistBundle); searchEncounterBundleBounded survives as the
// token-display read (medplum-tokendisplay.js).

const ENCOUNTER_GQL_FIELDS = `
  resourceType id status
  class { code }
  period { start }
  subject { reference resource { ... on Patient { resourceType id name { text given family } birthDate gender identifier { system value } } } }
  serviceProvider { display reference }
  participant { individual { display reference } }
  extension { url valueString }
`;

const COMPOSITION_GQL_FIELDS = `
  resourceType id status type { text }
  encounter { reference }
  section { code { coding { code } } text { div } }
`;

/**
 * FHIR GraphQL date-range filter for EncounterList over two real UTC instants
 * (ISO strings). The one shared building block for every date-window search in
 * this file — callers that only have clinic-local calendar dates (below) or a
 * real instant range (e.g. clinic-time.js's clinicDayBounds) both go through
 * this, so the `_filter:"date ge … and date <op> …"` string shape is written
 * in exactly one place (duplicate `date` args in the same `_filter` are invalid).
 */
export function buildDateRangeFilter(startInstant, endInstant, endOp = 'le') {
  return `_filter:"date ge '${startInstant}' and date ${endOp} '${endInstant}'"`;
}

/** FHIR GraphQL date-range filter for EncounterList (duplicate `date` args are invalid). */
export function buildTodayDateFilter(today) {
  return buildDateRangeFilter(`${today}T00:00:00Z`, `${today}T23:59:59Z`);
}

/** Prior-day window: [start, today) — same UTC slices as loadQueueHistoryFromMedplum. */
export function buildHistoryDateFilter(start, today) {
  return buildDateRangeFilter(`${start}T00:00:00Z`, `${today}T00:00:00Z`, 'lt');
}

function normalizeGraphqlEncounter(enc) {
  const ref = (enc.subject && enc.subject.reference)
    || (enc.subject && enc.subject.resource && enc.subject.resource.id ? 'Patient/' + enc.subject.resource.id : undefined);
  const out = { ...enc };
  if (ref) out.subject = { reference: ref };
  else if (out.subject) delete out.subject;
  return out;
}

/** Fold one GraphQL page into the same Bundle entry shape REST _include/_revinclude produces. */
function graphqlPageToBundle(encounters, compositions) {
  const encIdSet = new Set();
  const entries = [];
  for (const raw of encounters) {
    const enc = normalizeGraphqlEncounter(raw);
    entries.push({ resource: enc });
    encIdSet.add('Encounter/' + enc.id);
    const p = raw.subject && raw.subject.resource;
    if (p && p.resourceType === 'Patient') entries.push({ resource: p });
  }
  for (const comp of compositions) {
    const ref = comp.encounter && comp.encounter.reference;
    if (ref && encIdSet.has(ref)) entries.push({ resource: comp });
  }
  return { resourceType: 'Bundle', type: 'searchset', entry: entries };
}

/**
 * Fetch the intake Compositions for exactly the encounters on this page, in ONE
 * bounded GraphQL call. `CompositionList(encounter:"Encounter/a,Encounter/b,…")`
 * uses the standard FHIR `encounter` reference search parameter (comma = OR) —
 * Medplum's FHIR GraphQL exposes every REST search param on `<Type>List`
 * (snake_case; `encounter` is already snake_case). This replaces the former
 * unfiltered `CompositionList(_count:1000)` that pulled EVERY Composition in the
 * project and discarded all but the page's — an O(all-compositions) read.
 * Returns [] without a round-trip when the page has no encounters.
 */
async function fetchCompositionsForEncounters(medplum, encounterIds) {
  if (!encounterIds.length) return [];
  const refs = encounterIds.map((id) => 'Encounter/' + id).join(',');
  const query = `{
    CompositionList(encounter:"${refs}", _count:${SEARCH_PAGE_SIZE}) { ${COMPOSITION_GQL_FIELDS} }
  }`;
  const res = await medplum.graphql(query);
  return (res && res.data && res.data.CompositionList) || [];
}

/** Paged EncounterList + per-page CompositionList via $graphql — same budget as REST bounded search. */
export async function searchEncounterBundleGraphqlBounded(medplum, dateFilterArg, { maxPages = SEARCH_MAX_PAGES } = {}) {
  const allEntries = [];
  let offset = 0;
  let truncated = false;
  let primaryCount = 0;
  const pageBudget = Math.max(1, maxPages);

  for (let page = 0; page < pageBudget; page++) {
    const query = `{
      EncounterList(${dateFilterArg}, _count:${SEARCH_PAGE_SIZE}, _offset:${offset}) { ${ENCOUNTER_GQL_FIELDS} }
    }`;
    const res = await medplum.graphql(query);
    const encs = (res && res.data && res.data.EncounterList) || [];
    // Bound the Composition read to just this page's encounters (was: all).
    const comps = await fetchCompositionsForEncounters(medplum, encs.map((e) => e.id).filter(Boolean));
    const entries = (graphqlPageToBundle(encs, comps).entry) || [];
    allEntries.push(...entries);

    const pagePrimary = countPrimary(entries, 'Encounter');
    primaryCount += pagePrimary;

    if (pagePrimary < SEARCH_PAGE_SIZE) break;
    if (page >= pageBudget - 1) {
      truncated = true;
      break;
    }
    offset += SEARCH_PAGE_SIZE;
  }

  return {
    bundle: { resourceType: 'Bundle', type: 'searchset', entry: allEntries },
    truncated,
    fetchedCount: primaryCount,
    maxRows: SEARCH_MAX_ROWS,
  };
}

/**
 * Worklist read: GraphQL fan-in (two bounded round-trips per page — encounters,
 * then their compositions). FAIL LOUD — a $graphql
 * error surfaces to the caller instead of silently degrading to a REST re-query.
 * We self-host and control every deployed Medplum version (local Docker + AWS demo,
 * both current), so there is no "older server" failure mode to catch; a GraphQL
 * error means a real bug (query/schema drift) and must not be masked (AGENTS §1.3).
 * `baseQuery` is retained in the signature for call-site symmetry with the REST path.
 * @param {string} dateFilterArg  GraphQL EncounterList filter from buildTodayDateFilter / buildHistoryDateFilter
 */
export async function loadEncounterWorklistBundle(medplum, { dateFilterArg }, opts) {
  return searchEncounterBundleGraphqlBounded(medplum, dateFilterArg, opts);
}
